import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { fmtError } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import useManufacturers from "@/hooks/useManufacturers";
import CustomersTabs from "@/components/CustomersTabs";
import StateDistrictPicker from "@/components/StateDistrictPicker";
import { useDuplicateCheck } from "@/components/DuplicateContactDialog";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Plus, Search, UserPlus, Building2, Mail, Phone, Sparkles,
  CheckCircle2, ArrowRightCircle, MapPin, Pencil, Trash2,
} from "lucide-react";
import WhatsAppButton from "@/components/WhatsAppButton";
import CallButton from "@/components/CallButton";
import MultiSelectProducts from "@/components/MultiSelectProducts";
import { MultiSelectServices, MultiSelectTrainings } from "@/components/MultiSelectCatalog";
import EmployeeFilter from "@/components/EmployeeFilter";
import StateDistrictFilter from "@/components/StateDistrictFilter";
import ManufacturerFilter from "@/components/ManufacturerFilter";
import EditUnifiedContactDialog from "@/components/EditUnifiedContactDialog";
import { useAuth } from "@/contexts/AuthContext";
import { usePullToRefresh } from "@/hooks/usePullToRefresh";

import {
  LEAD_INDUSTRIES, LEAD_SOURCES, LEAD_APPLICATIONS, LEAD_OUTREACH,
  LEAD_POTENTIALS, LEAD_FOLLOWUP_STAGES,
  SOURCE_LABEL, INDUSTRY_LABEL, APPLICATION_LABEL,
  OUTREACH_LABEL, POTENTIAL_LABEL, FOLLOWUP_LABEL,
} from "@/lib/leadOptions";

// Channel-family colour palette — keyed off the new Excel-driven sources.
const SOURCE_COLOR = {
  // Strategic outbound
  lead_growth_plan: "bg-primary-50 text-primary-700 border-primary-200",
  // Warm / referral
  crm_contacts: "bg-zinc-100 text-zinc-700 border-zinc-200",
  existing_user: "bg-primary-50 text-primary-700 border-primary-200",
  existing_user_referral: "bg-emerald-50 text-emerald-700 border-emerald-200",
  team_referral: "bg-emerald-50 text-emerald-700 border-emerald-200",
  repeat_opportunities: "bg-emerald-50 text-emerald-700 border-emerald-200",
  // Training / service-led
  onsite_training: "bg-amber-50 text-amber-800 border-amber-200",
  product_support: "bg-amber-50 text-amber-800 border-amber-200",
  certification_course: "bg-amber-50 text-amber-800 border-amber-200",
  // Digital / paid
  digital_marketing: "bg-sky-50 text-sky-700 border-sky-200",
  linkedin_campaign: "bg-blue-50 text-blue-700 border-blue-200",
  renviro_website: "bg-emerald-50 text-emerald-700 border-emerald-200",
  // Inbound
  inbound_enquiry: "bg-emerald-50 text-emerald-700 border-emerald-200",
  customer_visit: "bg-orange-50 text-orange-800 border-orange-200",
  // Events
  webinars: "bg-violet-50 text-violet-700 border-violet-200",
  exhibition_conference: "bg-violet-50 text-violet-700 border-violet-200",
  // Marketplaces
  gem_indiamart: "bg-blue-50 text-blue-700 border-blue-200",
  // Growth motions
  cross_selling: "bg-rose-50 text-rose-800 border-rose-200",
  expansion_project: "bg-rose-50 text-rose-800 border-rose-200",
  multi_location_industries: "bg-rose-50 text-rose-800 border-rose-200",
  // Catch-all
  other_sources: "bg-zinc-100 text-zinc-500 border-zinc-200",
};

const STATUS_LABEL = {
  new: "New",
  contacted: "Contacted",
  qualified: "Qualified",
  converted: "Converted",
  dropped: "Dropped",
};

const STATUS_COLOR = {
  new: "bg-slate-100 text-slate-700 border-slate-200",
  contacted: "bg-amber-50 text-amber-800 border-amber-200",
  qualified: "bg-violet-50 text-violet-700 border-violet-200",
  converted: "bg-primary-50 text-primary-700 border-primary-200",
  dropped: "bg-zinc-100 text-zinc-500 border-zinc-200",
};

// Map every lead source → the most contextually appropriate WhatsApp / Email template.
const SITUATION_FOR_SOURCE = {
  // Strategic outbound — formal first-touch
  lead_growth_plan: "cold_outreach",
  digital_marketing: "cold_outreach",
  linkedin_campaign: "cold_outreach",
  gem_indiamart: "cold_outreach",
  crm_contacts: "cold_outreach",
  // Events / training
  webinars: "event_intro",
  exhibition_conference: "event_intro",
  onsite_training: "event_intro",
  certification_course: "event_intro",
  customer_visit: "event_intro",
  // Warm — referral or already-engaged
  inbound_enquiry: "reference_intro",
  existing_user: "reference_intro",
  existing_user_referral: "reference_intro",
  team_referral: "reference_intro",
  repeat_opportunities: "reference_intro",
  product_support: "reference_intro",
  renviro_website: "reference_intro",
  cross_selling: "reference_intro",
  expansion_project: "reference_intro",
  multi_location_industries: "reference_intro",
  other_sources: "cold_outreach",
};

function NewLeadDialog({ open, onOpenChange, onCreated, prefill }) {
  const [form, setForm] = useState({
    name: "", designation: "", company_name: "", email: "", phone: "",
    source: "lead_growth_plan", status: "new", event_name: "",
    industry: "", application: "", outreach_activity: "",
    potential: "", follow_up_stage: "",
    location: "", state: "", district: "", notes: "", manufacturer_id: "",
  });
  // Prefill from a client page ("New Lead" button) — applied each time the
  // dialog opens so the same customer can spawn fresh leads repeatedly.
  useEffect(() => {
    if (open && prefill && Object.values(prefill).some(Boolean)) {
      setForm((p) => ({ ...p, ...Object.fromEntries(Object.entries(prefill).filter(([, v]) => v)) }));
    }
    // eslint-disable-next-line
  }, [open]);
  const [saving, setSaving] = useState(false);
  const mfrs = useManufacturers();
  // Cross-CRM duplicate check — pops a modal if the name/phone/email/company
  // fuzzy-match an existing person across leads/contacts/clients.
  const dup = useDuplicateCheck("lead");

  const doSave = async () => {
    setSaving(true);
    try {
      await api.post("/leads", { ...form, manufacturer_id: form.manufacturer_id || null });
      toast.success("Lead added");
      onCreated?.();
      onOpenChange(false);
      setForm({
        name: "", designation: "", company_name: "", email: "", phone: "",
        source: "lead_growth_plan", status: "new", event_name: "",
        industry: "", application: "", outreach_activity: "",
        potential: "", follow_up_stage: "",
        location: "", state: "", district: "", notes: "", manufacturer_id: "",
      });
    } catch (err) { toast.error(fmtError(err)); } finally { setSaving(false); }
  };

  const submit = async () => {
    if (!form.name || !form.name.trim()) { toast.error("Name is required"); return; }
    if (!form.industry) { toast.error("Industry Sector is required."); return; }
    dup.guard({ name: form.name, email: form.email, phone: form.phone, company_name: form.company_name }, doSave);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-2xl font-extrabold">New marketing lead</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label className="text-xs font-semibold">Name *</Label>
            <Input className="rounded-2xl h-11 mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Mr. Rakesh Mehta" data-testid="lead-name-input" />
          </div>
          <div>
            <Label className="text-xs font-semibold">Designation</Label>
            <Input className="rounded-2xl h-11 mt-1.5" value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} placeholder="Plant Head" />
          </div>
          <div>
            <Label className="text-xs font-semibold">Company</Label>
            <Input className="rounded-2xl h-11 mt-1.5" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder="Acme Steel" data-testid="lead-company-input" />
          </div>
          <div>
            <Label className="text-xs font-semibold">Phone</Label>
            <Input className="rounded-2xl h-11 mt-1.5" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91 98765 43210" data-testid="lead-phone-input" />
          </div>
          <div>
            <Label className="text-xs font-semibold">Email</Label>
            <Input className="rounded-2xl h-11 mt-1.5" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="rakesh@acme.in" />
          </div>
          <div>
            <Label className="text-xs font-semibold">Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger className="rounded-2xl h-11 mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-2xl">
                {["new", "contacted", "qualified", "dropped"].map((k) => <SelectItem key={k} value={k}>{STATUS_LABEL[k]}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {/* === Excel-driven canonical dropdowns === */}
          <div>
            <Label className="text-xs font-semibold">Industry Sector *</Label>
            <Select value={form.industry || "__none"} onValueChange={(v) => setForm({ ...form, industry: v === "__none" ? "" : v })}>
              <SelectTrigger className="rounded-2xl h-11 mt-1.5" data-testid="lead-industry-select"><SelectValue placeholder="Pick industry…" /></SelectTrigger>
              <SelectContent className="rounded-2xl max-h-72">
                {LEAD_INDUSTRIES.map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold">Lead Source *</Label>
            <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
              <SelectTrigger className="rounded-2xl h-11 mt-1.5" data-testid="lead-source-select"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-2xl max-h-72">
                {LEAD_SOURCES.map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold">Application</Label>
            <Select value={form.application || "__none"} onValueChange={(v) => setForm({ ...form, application: v === "__none" ? "" : v })}>
              <SelectTrigger className="rounded-2xl h-11 mt-1.5" data-testid="lead-application-select"><SelectValue placeholder="Pick application…" /></SelectTrigger>
              <SelectContent className="rounded-2xl">
                <SelectItem value="__none">— Unspecified</SelectItem>
                {LEAD_APPLICATIONS.map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold">Outreach Activity</Label>
            <Select value={form.outreach_activity || "__none"} onValueChange={(v) => setForm({ ...form, outreach_activity: v === "__none" ? "" : v })}>
              <SelectTrigger className="rounded-2xl h-11 mt-1.5" data-testid="lead-outreach-select"><SelectValue placeholder="How did you reach out?" /></SelectTrigger>
              <SelectContent className="rounded-2xl">
                <SelectItem value="__none">— Unspecified</SelectItem>
                {LEAD_OUTREACH.map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold">Potential (interest)</Label>
            <Select value={form.potential || "__none"} onValueChange={(v) => setForm({ ...form, potential: v === "__none" ? "" : v })}>
              <SelectTrigger className="rounded-2xl h-11 mt-1.5" data-testid="lead-potential-select"><SelectValue placeholder="What are they interested in?" /></SelectTrigger>
              <SelectContent className="rounded-2xl max-h-72">
                <SelectItem value="__none">— Unspecified</SelectItem>
                {LEAD_POTENTIALS.map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold">Follow-up Stage</Label>
            <Select value={form.follow_up_stage || "__none"} onValueChange={(v) => setForm({ ...form, follow_up_stage: v === "__none" ? "" : v })}>
              <SelectTrigger className="rounded-2xl h-11 mt-1.5" data-testid="lead-followup-stage-select"><SelectValue placeholder="Next step…" /></SelectTrigger>
              <SelectContent className="rounded-2xl">
                <SelectItem value="__none">— Unspecified</SelectItem>
                {LEAD_FOLLOWUP_STAGES.map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label className="text-xs font-semibold">Event / campaign name</Label>
            <Input className="rounded-2xl h-11 mt-1.5" value={form.event_name} onChange={(e) => setForm({ ...form, event_name: e.target.value })} placeholder="ACMA Reliability Conclave 2026" />
          </div>
          <div className="col-span-2">
            <StateDistrictPicker
              state={form.state || ""}
              district={form.district || ""}
              onChange={({ state, district }) => setForm({ ...form, state, district })}
              testId="lead"
            />
          </div>
          <div className="col-span-2">
            <Label className="text-xs font-semibold">Supplier</Label>
            <Select value={form.manufacturer_id || "__none"} onValueChange={(v) => setForm({ ...form, manufacturer_id: v === "__none" ? "" : v })}>
              <SelectTrigger className="rounded-2xl h-11 mt-1.5" data-testid="lead-supplier-select"><SelectValue placeholder="Which supplier are they interested in?" /></SelectTrigger>
              <SelectContent className="rounded-2xl">
                <SelectItem value="__none">Not specified yet</SelectItem>
                {mfrs.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label className="text-xs font-semibold">Notes</Label>
            <Textarea className="rounded-2xl mt-1.5" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="What did you discuss / context…" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-2xl" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="rounded-2xl" data-testid="lead-save-button">{saving ? "Saving…" : "Add lead"}</Button>
        </DialogFooter>
        <dup.Dialog />
      </DialogContent>
    </Dialog>
  );
}

function ConvertLeadDialog({ open, onOpenChange, lead, onConverted }) {
  // Same form layout & auto-naming behaviour as the standalone New Deal
  // dialog in /pages/Deals.jsx — the only difference is the Client is
  // derived from the lead's company (matched if it already exists in the
  // CRM, otherwise auto-created on submit) instead of being picked manually.
  const [pipelineType, setPipelineType] = useState("product");
  const firstStage = pipelineType === "training" ? "enquiry" : "qualification";
  const [form, setForm] = useState({
    title: "",
    value: "",
    next_followup_date: "",
    next_action: "",
    notes: "",
    product_ids: [],
    service_ids: [],
  });
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [matchedClient, setMatchedClient] = useState(null); // populated if lead.company_name matches an existing client
  const [saving, setSaving] = useState(false);
  const [autoTitle, setAutoTitle] = useState(true);
  const { user } = useAuth();

  // Resolve company name we'll display & feed into the auto-name. Falls
  // back to the contact's own name when company is blank on the lead.
  const companyName = lead?.company_name?.trim() || lead?.name?.trim() || "";

  useEffect(() => {
    if (open && lead) {
      Promise.all([
        api.get("/clients").then((r) => r.data).catch(() => []),
        api.get("/products").then((r) => r.data).catch(() => []),
        api.get("/services").then((r) => r.data).catch(() => []),
      ]).then(([cs, ps, svs]) => {
        setClients(cs);
        setProducts(ps);
        setServices(svs);
        // Case-insensitive lookup so reps don't accidentally create a
        // duplicate client when the lead's company already exists.
        const m = cs.find(
          (c) => (c.company_name || "").trim().toLowerCase() === companyName.toLowerCase(),
        );
        setMatchedClient(m || null);
      });
      setPipelineType("product");
      setForm({
        title: "",
        value: "",
        next_followup_date: "",
        next_action: "",
        notes: lead.notes || `Converted from marketing lead (${lead.source})`,
        product_ids: [],
        service_ids: [],
      });
      setAutoTitle(true);
    }
  }, [open, lead, companyName]);

  // Auto-build deal name "[Item] - [Salesperson] - [Company]" — chooses
  // from the picker that's actually visible (products vs services vs
  // trainings) so naming stays consistent across pipelines.
  useEffect(() => {
    if (!open || !autoTitle) return;
    let firstName = "";
    if (pipelineType === "product") {
      firstName = products.find((p) => p.id === (form.product_ids || [])[0])?.name || "";
    } else {
      firstName = services.find((s) => s.id === (form.service_ids || [])[0])?.name || "";
    }
    const fallbackLabel = pipelineType === "training" ? "Training"
      : pipelineType === "service" ? "Service"
      : "Product";
    const picked = pipelineType === "product" ? (form.product_ids || []) : (form.service_ids || []);
    const subject = firstName || (picked.length ? fallbackLabel : "");
    const parts = [subject, user?.name, companyName].filter(Boolean);
    if (parts.length >= 2) {
      setForm((f) => ({ ...f, title: parts.join(" - ") }));
    }
  }, [open, autoTitle, pipelineType, form.product_ids, form.service_ids, products, services, user, companyName]);

  const submit = async () => {
    if (!lead) return;
    if (!form.title) { toast.error("Title required"); return; }
    setSaving(true);
    try {
      const res = await api.post(`/leads/${lead.id}/convert`, {
        deal_title: form.title,
        deal_value: Number(form.value) || 0,
        product_ids: pipelineType === "product" ? (form.product_ids || []) : [],
        service_ids: pipelineType !== "product" ? (form.service_ids || []) : [],
        pipeline_type: pipelineType,
        next_followup_date: form.next_followup_date
          ? new Date(form.next_followup_date + "T12:00:00").toISOString()
          : null,
        next_action: form.next_action || null,
        existing_client_id: matchedClient?.id || null,
      });
      const ptLabel = pipelineType.charAt(0).toUpperCase() + pipelineType.slice(1);
      toast.success(`Converted → ${ptLabel} pipeline deal #${(res.data?.deal?.id || "").slice(0, 6)}`);
      onConverted?.(res.data);
      onOpenChange(false);
    } catch (err) { toast.error(fmtError(err)); } finally { setSaving(false); }
  };

  if (!lead) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-lg max-h-[90vh] overflow-y-auto" data-testid="convert-lead-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl font-extrabold">Convert to deal</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {/* Pipeline picker — same options & UX as the standalone New Deal dialog. */}
          <div className="col-span-2">
            <Label className="text-xs font-semibold">Pipeline *</Label>
            <div className="grid grid-cols-3 gap-2 mt-1.5" data-testid="convert-pipeline-type">
              {[
                { v: "product",  l: "Product",  d: "Equipment sale" },
                { v: "service",  l: "Service",  d: "On-site / analysis" },
                { v: "training", l: "Training", d: "Workshop / course" },
              ].map((opt) => (
                <button
                  type="button"
                  key={opt.v}
                  onClick={() => setPipelineType(opt.v)}
                  data-testid={`convert-pipeline-type-${opt.v}`}
                  className={`p-2.5 rounded-2xl border text-left tap transition ${
                    pipelineType === opt.v
                      ? "border-primary bg-primary-50 ring-2 ring-primary/30"
                      : "border-border/60 hover:bg-muted/30"
                  }`}
                >
                  <p className="font-semibold text-sm">{opt.l}</p>
                  <p className="text-[10px] text-muted-foreground leading-snug mt-0.5">{opt.d}</p>
                </button>
              ))}
            </div>
            <p className="text-[10px] text-muted-foreground mt-1.5 leading-snug">Card will appear in the selected pipeline's first stage.</p>
          </div>

          <div className="col-span-2">
            <Label className="text-xs font-semibold">Title *</Label>
            <Input
              className="rounded-2xl h-11 mt-1.5"
              value={form.title}
              onChange={(e) => { setAutoTitle(false); setForm({ ...form, title: e.target.value }); }}
              data-testid="convert-deal-title"
              placeholder="Pick products below — auto-generated, editable"
            />
            <p className="text-[10px] text-muted-foreground mt-1 leading-snug">Auto-fills as &ldquo;Product - Salesperson - Company&rdquo;. Edit freely.</p>
          </div>

          {/* Client — derived from the lead. Shown as read-only with a
              helpful hint about whether it matches an existing CRM client
              or will be created fresh on submit. */}
          <div className="col-span-2">
            <Label className="text-xs font-semibold">Client</Label>
            <div
              className="rounded-2xl h-11 mt-1.5 px-3 flex items-center justify-between bg-muted/40 border border-border/60 text-sm"
              data-testid="convert-client-locked"
            >
              <span className="font-semibold truncate">{companyName || "—"}</span>
              <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ml-2 ${matchedClient ? "border-emerald-300 text-emerald-700 bg-emerald-50" : "border-amber-300 text-amber-700 bg-amber-50"}`}>
                {matchedClient ? "Existing" : "Will be created"}
              </Badge>
            </div>
            <p className="text-[10px] text-muted-foreground mt-1 leading-snug">Derived from the lead. {matchedClient ? "We'll link the new deal to this existing client." : "A new Client + Contact will be auto-created on convert."}</p>
          </div>

          <div>
            <Label className="text-xs font-semibold">Value (₹)</Label>
            <Input className="rounded-2xl h-11 mt-1.5" type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} placeholder="700000" data-testid="convert-deal-value" />
          </div>
          <div>
            <Label className="text-xs font-semibold">Starts in</Label>
            <div className="rounded-2xl h-11 mt-1.5 px-3 flex items-center bg-muted/60 border border-border/60 text-sm font-semibold text-muted-foreground" data-testid="convert-stage-locked">
              {firstStage === "enquiry" ? "Enquiry" : "Qualification"}
            </div>
            <p className="text-[10px] text-muted-foreground mt-1.5 leading-snug">All new deals begin at the first pipeline stage. Advance them as they progress.</p>
          </div>
          <div>
            <Label className="text-xs font-semibold">Next follow-up</Label>
            <Input type="date" className="rounded-2xl h-11 mt-1.5" value={form.next_followup_date} onChange={(e) => setForm({ ...form, next_followup_date: e.target.value })} data-testid="convert-deal-followup" />
          </div>
          <div>
            <Label className="text-xs font-semibold">Next action</Label>
            <Input className="rounded-2xl h-11 mt-1.5" value={form.next_action} onChange={(e) => setForm({ ...form, next_action: e.target.value })} placeholder="Send proposal" data-testid="convert-deal-action" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs font-semibold">
              {pipelineType === "training" ? "Trainings" : pipelineType === "service" ? "Technical services" : "Products"}
            </Label>
            {pipelineType === "product" && (
              <MultiSelectProducts
                value={form.product_ids}
                onChange={(ids) => setForm({ ...form, product_ids: ids })}
                testId="convert-deal-products"
                placeholder="Select one or more products…"
              />
            )}
            {pipelineType === "service" && (
              <MultiSelectServices
                value={form.service_ids}
                onChange={(ids) => setForm({ ...form, service_ids: ids })}
                testId="convert-deal-services"
              />
            )}
            {pipelineType === "training" && (
              <MultiSelectTrainings
                value={form.service_ids}
                onChange={(ids) => setForm({ ...form, service_ids: ids })}
                testId="convert-deal-trainings"
              />
            )}
            <p className="text-[10px] text-muted-foreground mt-1.5 leading-snug">
              {pipelineType === "training"
                ? "Pulled from the Training catalogue. Add new trainings under Products → Services tab."
                : pipelineType === "service"
                ? "Pulled from the Technical Services catalogue. Add new services under Products → Services tab."
                : "Pulled from the Product catalogue."}
            </p>
          </div>
          <div className="col-span-2">
            <Label className="text-xs font-semibold">Notes</Label>
            <Textarea className="rounded-2xl mt-1.5" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="convert-deal-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-2xl" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="rounded-2xl" data-testid="convert-confirm-button">
            {saving ? "Converting…" : <><ArrowRightCircle className="h-4 w-4 mr-1" /> Convert</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LeadRow({ lead, onChanged, onConvert, onEdit }) {
  const situationKey = SITUATION_FOR_SOURCE[lead.source] || "cold_outreach";
  const setStatus = async (status) => {
    try {
      await api.patch(`/leads/${lead.id}`, { ...lead, status });
      toast.success(`Marked ${STATUS_LABEL[status]}`);
      onChanged?.();
    } catch (err) { toast.error(fmtError(err)); }
  };
  const onDelete = async () => {
    if (!window.confirm(`Delete lead "${lead.name}" (${lead.company_name || "no company"})? This cannot be undone.`)) return;
    try {
      await api.delete(`/leads/${lead.id}`);
      toast.success("Lead deleted");
      onChanged?.();
    } catch (err) { toast.error(fmtError(err)); }
  };

  return (
    <div className="p-4 rounded-2xl border border-border/60 hover:bg-muted/20 transition" data-testid={`lead-row-${lead.id}`}>
      <div className="flex items-start gap-3 flex-wrap">
        <div className="h-10 w-10 rounded-2xl bg-primary-50 text-primary-700 grid place-items-center shrink-0">
          <UserPlus className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-sm truncate">{lead.name}</p>
            <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${SOURCE_COLOR[lead.source] || ""}`}>
              {SOURCE_LABEL[lead.source] || lead.source}
            </Badge>
            <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${STATUS_COLOR[lead.status] || ""}`}>
              {STATUS_LABEL[lead.status] || lead.status}
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground truncate mt-0.5">
            {lead.designation && <span>{lead.designation} · </span>}
            {lead.company_name || "—"}
          </p>
          <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
            {lead.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{lead.phone}</span>}
            {lead.email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{lead.email}</span>}
            {(() => {
              const loc = [lead.district, lead.state].filter(Boolean).join(", ") || lead.location;
              return loc ? <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{loc}</span> : null;
            })()}
            {lead.event_name && <span className="inline-flex items-center gap-1"><Sparkles className="h-3 w-3" />{lead.event_name}</span>}
            {(lead.created_by_name || lead.owner_name) && (
              <span className="inline-flex items-center gap-1" data-testid={`lead-owner-${lead.id}`}>
                <UserPlus className="h-3 w-3" />Created by: <strong className="text-foreground/70">{lead.created_by_name || lead.owner_name}</strong>
              </span>
            )}
          </div>
          {/* Excel-driven attribute chips — Industry · Application · Potential ·
              Follow-up stage. Hidden if every field is blank to keep the
              row compact on legacy data. */}
          {(lead.industry || lead.application || lead.potential || lead.outreach_activity || lead.follow_up_stage) && (
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`lead-attrs-${lead.id}`}>
              {lead.industry && (
                <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-700 border-slate-200">
                  {INDUSTRY_LABEL[lead.industry] || lead.industry}
                </Badge>
              )}
              {lead.application && (
                <Badge variant="outline" className="text-[10px] bg-violet-50 text-violet-700 border-violet-200">
                  {APPLICATION_LABEL[lead.application] || lead.application}
                </Badge>
              )}
              {lead.potential && (
                <Badge variant="outline" className="text-[10px] bg-primary-50 text-primary-700 border-primary-200">
                  {POTENTIAL_LABEL[lead.potential] || lead.potential}
                </Badge>
              )}
              {lead.outreach_activity && (
                <Badge variant="outline" className="text-[10px] bg-sky-50 text-sky-700 border-sky-200">
                  {OUTREACH_LABEL[lead.outreach_activity] || lead.outreach_activity}
                </Badge>
              )}
              {lead.follow_up_stage && (
                <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-800 border-amber-200">
                  {FOLLOWUP_LABEL[lead.follow_up_stage] || lead.follow_up_stage}
                </Badge>
              )}
            </div>
          )}
        </div>
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold whitespace-nowrap">
          {fmtDate(lead.created_at)}
        </span>
      </div>

      <div className="mt-3 flex items-center gap-2 flex-wrap">
        {lead.phone && <CallButton phone={lead.phone} testId={`lead-call-${lead.id}`} />}
        {(lead.phone || lead.email) && (
          <WhatsAppButton
            phone={lead.phone}
            email={lead.email}
            contactName={lead.name}
            clientCompany={lead.company_name}
            defaultTemplate={situationKey}
            event={lead.event_name}
            label={situationKey === "event_intro" ? "Event message" : situationKey === "reference_intro" ? "Reference msg" : "Outreach"}
            testId={`lead-wa-${lead.id}`}
          />
        )}

        <Button
          variant="ghost"
          size="sm"
          onClick={() => onEdit?.(lead)}
          className="rounded-2xl tap text-xs text-muted-foreground hover:text-foreground"
          data-testid={`lead-edit-${lead.id}`}
        >
          <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
        </Button>

        <Button
          variant="ghost"
          size="sm"
          onClick={onDelete}
          className="rounded-2xl tap text-xs text-muted-foreground hover:text-red-600"
          data-testid={`lead-delete-${lead.id}`}
        >
          <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
        </Button>

        {lead.status !== "contacted" && lead.status !== "qualified" && lead.status !== "converted" && (
          <Button variant="ghost" size="sm" onClick={() => setStatus("contacted")} className="rounded-2xl tap text-xs text-muted-foreground hover:text-foreground" data-testid={`lead-mark-contacted-${lead.id}`}>
            <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Mark contacted
          </Button>
        )}
        {lead.status === "contacted" && (
          <Button variant="ghost" size="sm" onClick={() => setStatus("qualified")} className="rounded-2xl tap text-xs text-muted-foreground hover:text-foreground" data-testid={`lead-mark-qualified-${lead.id}`}>
            <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Mark qualified
          </Button>
        )}

        {lead.status !== "converted" ? (
          <Button
            size="sm"
            onClick={() => onConvert(lead)}
            className="rounded-2xl tap text-xs ml-auto"
            data-testid={`lead-convert-${lead.id}`}
          >
            <ArrowRightCircle className="h-3.5 w-3.5 mr-1" /> Convert to deal
          </Button>
        ) : (
          lead.converted_to_deal_id && (
            <Link
              to={`/deals/${lead.converted_to_deal_id}`}
              className="ml-auto text-xs font-bold text-primary inline-flex items-center gap-1 hover:underline"
              data-testid={`lead-deal-link-${lead.id}`}
            >
              <Building2 className="h-3 w-3" /> Open deal
            </Link>
          )
        )}
      </div>

      {lead.notes && (
        <p className="mt-2 text-[11px] italic text-muted-foreground bg-muted/30 rounded-xl px-2.5 py-1.5">"{lead.notes}"</p>
      )}
    </div>
  );
}

export default function Leads() {
  const [params] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [source, setSource] = useState("");
  const [status, setStatus] = useState("");
  const [manufacturerId, setManufacturerId] = useState("");
  const [openNew, setOpenNew] = useState(params.get("new") === "1");
  // Prefill carried from a client page's "New Lead" button (query params).
  const leadPrefill = {
    name: params.get("name") || "", company_name: params.get("company") || "",
    email: params.get("email") || "", phone: params.get("phone") || "",
    industry: params.get("industry") || "", source: params.get("source") || "",
  };
  const [convertLead, setConvertLead] = useState(null);
  // Inline edit — open the unified contact dialog with this lead pre-filled
  // so quick edits to name/phone/email/company/designation don't require
  // navigating away. Backend PATCH /leads/{id} hits the same endpoint.
  const [editLead, setEditLead] = useState(null);
  const [ownerId, setOwnerId] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [districtFilter, setDistrictFilter] = useState("");
  // 5 new Excel-driven filter slots — each mirrors a canonical dropdown
  // list and short-circuits to undefined when blank.
  const [industry, setIndustry] = useState("");
  const [application, setApplication] = useState("");
  const [outreach, setOutreach] = useState("");
  const [potential, setPotential] = useState("");
  const [followUpStage, setFollowUpStage] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/leads", {
        params: {
          q: q || undefined,
          source: source || undefined,
          status: status || undefined,
          owner_id: ownerId || undefined,
          manufacturer_id: manufacturerId || undefined,
          state: stateFilter || undefined,
          district: districtFilter || undefined,
          industry: industry || undefined,
          application: application || undefined,
          outreach_activity: outreach || undefined,
          potential: potential || undefined,
          follow_up_stage: followUpStage || undefined,
        },
      });
      setRows(data);
    } finally { setLoading(false); }
  }, [q, source, status, ownerId, manufacturerId, stateFilter, districtFilter, industry, application, outreach, potential, followUpStage]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);
  const { indicator: pullIndicator } = usePullToRefresh(load);

  // Bucketed by status for the summary tiles
  const byStatus = rows.reduce((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="p-5 sm:p-8 lg:p-10 max-w-[1280px] mx-auto">
      {pullIndicator}
      <CustomersTabs />
      <header className="mb-5 sm:mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-muted-foreground">Marketing pipeline</p>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-1">Leads.</h1>
          <p className="text-sm text-muted-foreground mt-1.5 max-w-md">
            Raw contacts collected from events, campaigns and references. Reach out, qualify, then convert into deals.
          </p>
        </div>
        <Button onClick={() => setOpenNew(true)} className="rounded-2xl tap" data-testid="new-lead-button">
          <Plus className="h-4 w-4 mr-1" /> New lead
        </Button>
      </header>

      {/* Status summary tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 sm:gap-4 mb-5">
        {["new", "contacted", "qualified", "converted", "dropped"].map((k) => (
          <button
            key={k}
            onClick={() => setStatus(status === k ? "" : k)}
            className={`p-2.5 sm:p-4 rounded-2xl border tap text-left transition ${
              status === k ? "bg-card border-primary shadow-soft" : "bg-muted/30 border-border/60"
            }`}
            data-testid={`lead-tile-${k}`}
          >
            <p className="text-[9px] sm:text-[10px] uppercase tracking-wider font-bold text-muted-foreground whitespace-nowrap">
              {STATUS_LABEL[k]}
            </p>
            <p className="font-display text-2xl sm:text-3xl font-black mt-0.5 sm:mt-1.5 leading-none">
              {byStatus[k] || 0}
            </p>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-2 sm:gap-3 mb-5 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, company, industry, event, notes…"
            className="pl-10 h-12 rounded-2xl bg-muted/40"
            data-testid="leads-search-input"
          />
        </div>
        <Select value={source || "__all"} onValueChange={(v) => setSource(v === "__all" ? "" : v)}>
          <SelectTrigger className="rounded-2xl h-12 w-44" data-testid="leads-source-filter"><SelectValue placeholder="All sources" /></SelectTrigger>
          <SelectContent className="rounded-2xl max-h-72">
            <SelectItem value="__all">All sources</SelectItem>
            {LEAD_SOURCES.map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={industry || "__all"} onValueChange={(v) => setIndustry(v === "__all" ? "" : v)}>
          <SelectTrigger className="rounded-2xl h-12 w-44" data-testid="leads-industry-filter"><SelectValue placeholder="All industries" /></SelectTrigger>
          <SelectContent className="rounded-2xl max-h-72">
            <SelectItem value="__all">All industries</SelectItem>
            {LEAD_INDUSTRIES.map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={application || "__all"} onValueChange={(v) => setApplication(v === "__all" ? "" : v)}>
          <SelectTrigger className="rounded-2xl h-12 w-44" data-testid="leads-application-filter"><SelectValue placeholder="All applications" /></SelectTrigger>
          <SelectContent className="rounded-2xl">
            <SelectItem value="__all">All applications</SelectItem>
            {LEAD_APPLICATIONS.map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={outreach || "__all"} onValueChange={(v) => setOutreach(v === "__all" ? "" : v)}>
          <SelectTrigger className="rounded-2xl h-12 w-44" data-testid="leads-outreach-filter"><SelectValue placeholder="All outreach" /></SelectTrigger>
          <SelectContent className="rounded-2xl">
            <SelectItem value="__all">All outreach</SelectItem>
            {LEAD_OUTREACH.map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={potential || "__all"} onValueChange={(v) => setPotential(v === "__all" ? "" : v)}>
          <SelectTrigger className="rounded-2xl h-12 w-44" data-testid="leads-potential-filter"><SelectValue placeholder="All potentials" /></SelectTrigger>
          <SelectContent className="rounded-2xl max-h-72">
            <SelectItem value="__all">All potentials</SelectItem>
            {LEAD_POTENTIALS.map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={followUpStage || "__all"} onValueChange={(v) => setFollowUpStage(v === "__all" ? "" : v)}>
          <SelectTrigger className="rounded-2xl h-12 w-44" data-testid="leads-followup-stage-filter"><SelectValue placeholder="All stages" /></SelectTrigger>
          <SelectContent className="rounded-2xl">
            <SelectItem value="__all">All stages</SelectItem>
            {LEAD_FOLLOWUP_STAGES.map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <ManufacturerFilter value={manufacturerId} onChange={setManufacturerId} testId="leads-supplier-filter" className="h-12 w-44 rounded-2xl" />
        <EmployeeFilter value={ownerId} onChange={setOwnerId} testId="leads-employee-filter" />
        <StateDistrictFilter
          state={stateFilter}
          district={districtFilter}
          onChange={({ state, district }) => { setStateFilter(state); setDistrictFilter(district); }}
          testId="leads-state-filter"
        />
      </div>

      <Card className="rounded-3xl border-border/60 shadow-soft">
        <CardContent className="p-3 sm:p-4 space-y-2">
          {loading ? (
            <p className="py-12 text-center text-sm text-muted-foreground">Loading…</p>
          ) : rows.length === 0 ? (
            <div className="py-14 text-center">
              <UserPlus className="h-7 w-7 text-primary mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No leads yet. Add your first event contact above.</p>
            </div>
          ) : (
            rows.map((lead) => (
              <LeadRow key={lead.id} lead={lead} onChanged={load} onConvert={setConvertLead} onEdit={setEditLead} />
            ))
          )}
        </CardContent>
      </Card>

      <NewLeadDialog open={openNew} onOpenChange={setOpenNew} onCreated={load} prefill={leadPrefill} />
      <ConvertLeadDialog
        open={!!convertLead}
        onOpenChange={(o) => { if (!o) setConvertLead(null); }}
        lead={convertLead}
        onConverted={load}
      />
      <EditUnifiedContactDialog
        open={!!editLead}
        onOpenChange={(o) => { if (!o) setEditLead(null); }}
        row={editLead ? { ...editLead, kind: "lead" } : null}
        onSaved={load}
      />
    </div>
  );
}
